import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Users, Mail, UserPlus, ShieldCheck, History, RefreshCw, Ban, CheckCircle2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const blank = { first_name: '', last_name: '', email: '', phone: '', password: '', staff_role: '' };

function Modal({ open, title, children, onClose, maxWidth = 'max-w-xl' }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className={`bg-white rounded-xl shadow-2xl w-full ${maxWidth} max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="font-semibold text-lg">{title}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100" aria-label="Close"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

export default function StaffManagement() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ staff: [], pending_invitations: [], summary: {} });
  const [roles, setRoles] = useState([]);
  const [permissionLabels, setPermissionLabels] = useState({});
  const [orgType, setOrgType] = useState('');
  const [mode, setMode] = useState(null); // invite | create | audit | edit
  const [form, setForm] = useState(blank);
  const [selected, setSelected] = useState(null);
  const [audit, setAudit] = useState([]);
  const [saving, setSaving] = useState(false);

  const roleMap = useMemo(() => Object.fromEntries(roles.map(r => [r.key, r])), [roles]);

  const load = async () => {
    setLoading(true);
    try {
      const [staffRes, roleRes] = await Promise.all([api.get('/staff'), api.get('/staff/roles')]);
      setData(staffRes.data);
      setRoles(roleRes.data.roles || []);
      setPermissionLabels(roleRes.data.permission_labels || {});
      setOrgType(roleRes.data.organization_type || '');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Unable to load staff access management');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openForm = (newMode) => {
    setForm(blank);
    setSelected(null);
    setMode(newMode);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.staff_role) return toast.error('Select a role');
    setSaving(true);
    try {
      if (mode === 'invite') {
        const res = await api.post('/staff/invite', form);
        if (res.data.email_sent) toast.success(`Invitation emailed to ${form.email}`);
        else toast.warning('Invitation created, but email delivery failed. Use Resend after email delivery is corrected.');
      } else if (mode === 'create') {
        await api.post('/staff/create', form);
        toast.success('Staff account created');
      } else if (mode === 'edit' && selected) {
        await api.patch(`/staff/${selected.id}`, { staff_role: form.staff_role, first_name: form.first_name, last_name: form.last_name, phone: form.phone });
        toast.success('Staff role updated');
      }
      setMode(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Unable to save staff account');
    } finally { setSaving(false); }
  };

  const editMember = (member) => {
    setSelected(member);
    setForm({
      first_name: member.first_name || '', last_name: member.last_name || '', email: member.email || '',
      phone: member.phone || '', password: '', staff_role: member.staff_role || ''
    });
    setMode('edit');
  };

  const toggleAccess = async (member) => {
    const revoked = member.status === 'revoked' || member.is_active === false;
    if (!revoked && !window.confirm(`Revoke access for ${member.email}?`)) return;
    try {
      await api.post(`/staff/${member.id}/${revoked ? 'reactivate' : 'revoke'}`);
      toast.success(revoked ? 'Staff access restored' : 'Staff access revoked');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Unable to update access'); }
  };

  const resend = async (invite) => {
    try {
      const res = await api.post(`/staff/invitations/${invite.id}/resend`);
      if (res.data.email_sent) toast.success(`Invitation resent to ${invite.email}`);
      else toast.warning('Invitation refreshed but email delivery failed');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Unable to resend invitation'); }
  };

  const showAudit = async () => {
    try {
      const res = await api.get('/staff/audit-log?limit=100');
      setAudit(res.data.audit_log || []);
      setMode('audit');
    } catch (e) { toast.error(e.response?.data?.detail || 'Unable to load audit log'); }
  };

  const summary = data.summary || {};
  const title = orgType === 'hoa' ? 'HOA team' : orgType === 'property_manager' ? 'property-management team' : 'landlord team';

  return (
    <div className="space-y-5" data-testid="staff-management">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2"><Users className="h-5 w-5 text-blue-600" /><h2 className="text-xl font-semibold">Staff & Access Management</h2></div>
          <p className="text-sm text-gray-500 mt-1">{summary.active || 0} active · {summary.pending || 0} pending · {summary.revoked || 0} revoked</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={showAudit}><History className="h-4 w-4 mr-2" />Audit Log</Button>
          <Button variant="outline" onClick={() => openForm('invite')}><Mail className="h-4 w-4 mr-2" />Invite</Button>
          <Button onClick={() => openForm('create')}><UserPlus className="h-4 w-4 mr-2" />Create Account</Button>
        </div>
      </div>

      {loading ? (
        <Card><CardContent className="py-16 text-center text-gray-500">Loading team access…</CardContent></Card>
      ) : data.staff.length === 0 && data.pending_invitations.length === 0 ? (
        <Card>
          <CardContent className="py-14 text-center">
            <Users className="h-12 w-12 mx-auto text-gray-300" />
            <h3 className="mt-4 font-medium">No staff members yet</h3>
            <p className="text-sm text-gray-500 mt-1">Add team members to help manage your {title}.</p>
            <div className="mt-5 flex justify-center gap-2">
              <Button variant="outline" onClick={() => openForm('invite')}><Mail className="h-4 w-4 mr-2" />Invite by Email</Button>
              <Button onClick={() => openForm('create')}><UserPlus className="h-4 w-4 mr-2" />Create Account</Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {data.pending_invitations.map(invite => (
            <Card key={`invite-${invite.id}`}>
              <CardContent className="p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="font-medium">{[invite.first_name, invite.last_name].filter(Boolean).join(' ') || invite.email}</div>
                  <div className="text-sm text-gray-500">{invite.email}</div>
                  <div className="mt-2 flex gap-2"><Badge variant="outline">{invite.staff_role_name || invite.staff_role}</Badge><Badge>Invitation Pending</Badge></div>
                </div>
                <Button variant="outline" onClick={() => resend(invite)}><RefreshCw className="h-4 w-4 mr-2" />Resend Invite</Button>
              </CardContent>
            </Card>
          ))}
          {data.staff.map(member => {
            const revoked = member.status === 'revoked' || member.is_active === false;
            return (
              <Card key={member.id}>
                <CardContent className="p-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <div className="font-medium">{[member.first_name, member.last_name].filter(Boolean).join(' ') || member.email}</div>
                    <div className="text-sm text-gray-500">{member.email}{member.phone ? ` · ${member.phone}` : ''}</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Badge variant="outline">{member.staff_role_name || roleMap[member.staff_role]?.name || member.staff_role || 'Staff'}</Badge>
                      <Badge className={revoked ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>{revoked ? 'Revoked' : 'Active'}</Badge>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => editMember(member)}><ShieldCheck className="h-4 w-4 mr-2" />Role</Button>
                    <Button variant="outline" onClick={() => toggleAccess(member)} className={revoked ? '' : 'text-red-600'}>
                      {revoked ? <CheckCircle2 className="h-4 w-4 mr-2" /> : <Ban className="h-4 w-4 mr-2" />}{revoked ? 'Reactivate' : 'Revoke'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Modal open={mode === 'invite' || mode === 'create' || mode === 'edit'} title={mode === 'invite' ? 'Invite Staff Member' : mode === 'edit' ? 'Update Staff Role' : 'Create Staff Account'} onClose={() => setMode(null)}>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>First Name *</Label><Input value={form.first_name} required onChange={e => setForm({...form, first_name:e.target.value})} /></div>
            <div><Label>Last Name *</Label><Input value={form.last_name} required onChange={e => setForm({...form, last_name:e.target.value})} /></div>
          </div>
          <div><Label>Email *</Label><Input type="email" value={form.email} required disabled={mode === 'edit'} onChange={e => setForm({...form, email:e.target.value})} /></div>
          <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone:e.target.value})} /></div>
          {mode === 'create' && <div><Label>Password * (minimum 8 characters)</Label><Input type="password" minLength={8} value={form.password} required onChange={e => setForm({...form, password:e.target.value})} /></div>}
          <div>
            <Label>Role *</Label>
            <select className="w-full mt-1 border rounded-md px-3 py-2 bg-white" value={form.staff_role} required onChange={e => setForm({...form, staff_role:e.target.value})}>
              <option value="">Select role…</option>
              {roles.map(role => <option key={role.key} value={role.key}>{role.name}</option>)}
            </select>
            {form.staff_role && roleMap[form.staff_role] && (
              <div className="mt-3 rounded-lg bg-slate-50 border p-3">
                <p className="text-sm font-medium">{roleMap[form.staff_role].name}</p>
                <p className="text-xs text-gray-600 mt-1">{roleMap[form.staff_role].description}</p>
                <div className="mt-2 flex flex-wrap gap-1">
                  {(roleMap[form.staff_role].permissions || []).slice(0, 10).map(p => <Badge key={p} variant="outline" className="text-[11px]">{p === '*' ? 'Full Access' : permissionLabels[p] || p}</Badge>)}
                  {(roleMap[form.staff_role].permissions || []).length > 10 && <Badge variant="outline">+{roleMap[form.staff_role].permissions.length - 10} more</Badge>}
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2"><Button type="button" variant="outline" onClick={() => setMode(null)}>Cancel</Button><Button disabled={saving} type="submit">{saving ? 'Saving…' : mode === 'invite' ? 'Send Invitation' : mode === 'edit' ? 'Save Role' : 'Create Account'}</Button></div>
        </form>
      </Modal>

      <Modal open={mode === 'audit'} title="Staff Audit Log" onClose={() => setMode(null)} maxWidth="max-w-3xl">
        <div className="space-y-2">
          {audit.length === 0 ? <p className="text-gray-500">No staff access activity yet.</p> : audit.map((row, i) => (
            <div key={row.id || i} className="border rounded-lg p-3">
              <div className="flex justify-between gap-3"><span className="font-medium text-sm">{String(row.action || '').replaceAll('_', ' ')}</span><span className="text-xs text-gray-500">{row.timestamp ? new Date(row.timestamp).toLocaleString() : ''}</span></div>
              <div className="text-xs text-gray-600 mt-1">{row.actor_email || 'System'}{row.target_email ? ` → ${row.target_email}` : ''}</div>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}
